// import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { makeFetchTransport } from "@sentry/react";
// import {
//     createRoutesFromChildren,
//     matchRoutes,
//     useLocation,
//     useNavigationType,
// } from "react-router-dom";

const getSentryEnvironment = (hostname: string): string => {
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("dev")) {
        return "dev";
    }
    return hostname
};

type SentryEnvironment = "qa" | "prod";

const getSentrySamplingEnvironment = (hostname: string): SentryEnvironment => {
    return hostname.includes("qa") ? "qa" : "prod";
};

type SentrySamplingConfig = {
    tracesSampleRate: number;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
};

const SENTRY_SAMPLING_BY_ENV: Record<SentryEnvironment, SentrySamplingConfig> = {
    qa: {
        tracesSampleRate: 0.5,
        replaysSessionSampleRate: 0.2,
        replaysOnErrorSampleRate: 0.5,
    },
    prod: {
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 0.5,
    },
};

const getSentryToken = (): string =>
    import.meta.env.VITE_SENTRY_TOKEN ||
    "https://ee2b0ed527fa439a10dce928f73264f5@o4511983789277184.ingest.us.sentry.io/4511983794061312";

const SENTRY_TUNNEL_PATH = "/v1/timekeeper/v1/log_handler";

const buildSentryTunnelUrl = (): string | null => {
    const gatewayBaseUrl = `${import.meta.env.VITE_TK_GW_DOMAIN || ""}`.replace(/\/+$/, "");
    if (!gatewayBaseUrl) {
        return null;
    }

    return `${gatewayBaseUrl}${SENTRY_TUNNEL_PATH}`;
};

const makeSentryTransport: typeof makeFetchTransport = (options) =>
    makeFetchTransport(options, (url: string, init: RequestInit) => {
        const token = localStorage.getItem("access_token");
        const headers = new Headers(init.headers);
        if (token) {
            headers.set("Authorization", `Bearer ${token}`);
        }
        return fetch(url, { ...init, headers });
    });

const isSentryEnabled = (): boolean => {
    const logEnabled = import.meta.env.LOG_ENABLED ?? import.meta.env.VITE_LOG_ENABLED;
    if (String(logEnabled).toLowerCase() === "true") {
        return true;
    }

    const hostname = window.location.hostname;
    const environment = getSentryEnvironment(hostname);
    return environment !== "dev";
};

const sentryToken = getSentryToken();
if (sentryToken) {
    const hostname = window.location.hostname;
    const environment = getSentryEnvironment(hostname);
    if (isSentryEnabled()) {
        const tunnelUrl = buildSentryTunnelUrl();
        const samplingEnvironment = getSentrySamplingEnvironment(hostname);
        const { tracesSampleRate, replaysSessionSampleRate, replaysOnErrorSampleRate } = SENTRY_SAMPLING_BY_ENV[samplingEnvironment];
        Sentry.init({
            dsn: sentryToken,
            environment: environment,
            tunnel: tunnelUrl || undefined,
            transport: tunnelUrl ? makeSentryTransport : undefined,
            integrations: [
                Sentry.browserTracingIntegration(),
                // Sentry.reactRouterV6BrowserTracingIntegration({
                //     useEffect,
                //     useLocation,
                //     useNavigationType,
                //     createRoutesFromChildren,
                //     matchRoutes,
                // }),
                Sentry.replayIntegration({
                    maskAllText: false,
                    maskAllInputs: false,
                    blockAllMedia: false,
                }),
                //   Sentry.debugIntegration(),
                Sentry.captureConsoleIntegration(),
                Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
                Sentry.contextLinesIntegration(),
                Sentry.httpClientIntegration(),
                //   Sentry.sessionTimingIntegration(),
            ],
            // Performance Monitoring
            tracesSampleRate: tracesSampleRate, //  Capture 100% of the transactions
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [
                "localhost",
                //     /^https:\/\/yourserver\.io\/api/,
                //     "127.0.0.1:8000",
                "qa-tk-dashboard-wtslus466q-uc.a.run.app",
                //     "ms-fastapi-gateway-164457384651.us-central1.run.app",
                //     "ms-fastapi-auth-164457384651.us-central1.run.app"
            ],

            // Session Replay
            replaysSessionSampleRate: replaysSessionSampleRate,
            replaysOnErrorSampleRate: replaysOnErrorSampleRate,
        });
        console.log(`Sentry initialized with environment: ${environment}`);
    }
}

